import { getConfigurations } from "@/Api/ConfigurationsApi";
import { getPermissions } from "@/Api/PermissonsApi";
import { getCurrentUsername } from "@/Api/UserApi";
import { PermissionType } from "@/Enums/PermissionType";
import { ConfigurationsOutput } from "@/Schemas/Configurations/ConfigurationsOutput";
import { getCompanyLogoUrl, getCompanyLogoAlt } from "@/Utils/environment";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import React, { createContext, useContext, useLayoutEffect, useState } from "react";
import { useTask } from "vig-ui-library";

export type AppProviderState = {
    configurations: ConfigurationsOutput;
    permissions: Set<PermissionType>;
    currentUsername: string;
}

const AppProviderContext = createContext<AppProviderState>({} as AppProviderState);

export type AppProviderProps = {
    children: React.ReactNode;
};

export const AppProvider = ({ children }: AppProviderProps) => {
    const [configurations, setConfigurations] = useState<ConfigurationsOutput | null>(null);

    const [permissions, setPermissions] = useState<Set<PermissionType>>(new Set());

    const [currentUsername, setCurrentUsername] = useState<string>("");

    const loadConfigurations = async () => {
        const configurationsResult = await getConfigurations();
        setConfigurations(configurationsResult);
    };

    const loadPermissions = async () => {
        const foundPermissions = await getPermissions();
        setPermissions(new Set(foundPermissions));
    }

    const loadCurrentUsername = async () => {
        const username = await getCurrentUsername();
        setCurrentUsername(username);
    }

    const loadAppTask = useTask(async () => {
        try {
            await Promise.all([loadConfigurations(), loadPermissions(), loadCurrentUsername()]);
        } catch (err) { }
    });

    useLayoutEffect(() => {
        loadAppTask.perform();
    }, []);

    if (loadAppTask.isRunning) return <ApplicationLoader />

    if (!configurations) return <ApplicationError />

    const providerState: AppProviderState = {
        configurations,
        permissions,
        currentUsername,
    }

    return (
        <AppProviderContext.Provider value={providerState}>
            {children}
        </AppProviderContext.Provider>
    );
}

export const useAppProvider = () => {
    const context = useContext(AppProviderContext);

    if (!context) {
        throw new Error("useAppProvider must be used within a AppProvider");
    }

    return context;
}

const flexCenter: React.CSSProperties = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
}

const fullScreen: React.CSSProperties = {
    width: "100vw",
    height: "100vh",
}

const ApplicationLoader = () => {
    return (
        <Box sx={{ ...flexCenter, ...fullScreen, gap: 2, p: 2 }}>
            <img src={getCompanyLogoUrl()} alt={getCompanyLogoAlt()} width="250" height="auto" />
            <Typography variant="body1" sx={{ marginTop: 3 }}>
                Načítavam aplikáciu...
            </Typography>
            <CircularProgress />
        </Box>
    )
}

const ApplicationError = () => {
    const theme = useTheme();

    return (
        <Box sx={{ ...flexCenter, ...fullScreen, gap: 2, p: 2, textAlign: "center" }}>
            <img src={getCompanyLogoUrl()} alt={getCompanyLogoAlt()} width="250" height="auto" />
            <Typography variant="h4" color={theme.palette.error.main} sx={{ maxWidth: 500, marginTop: 3 }}>
                Nastala chyba pri načítaní aplikácie.
            </Typography>
            <Typography variant="body2" sx={{ maxWidth: 500 }}>
                Prosím skontrolujte pripojenie k internetu a obnovte stránku.
                Ak problém pretrváva, kontaktujte IT.
            </Typography>
        </Box>
    )
}